import { useCallback, useEffect, useRef } from 'react';

import { toaster } from 'components/toaster/toaster';
import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';

import { Component } from './types';

type AlertComponent = Component | null | undefined;

// This is a long duration to ensure the user has time to read the alert.
const TOAST_DURATION = 20000;

/**
 * Displays the Sanity Alert document from the feature home page as a toast.
 * Shows on initial data load and every time the user returns to the app.
 */
export const useAlertToast = (alertComponent: AlertComponent) => {
  const alertRef = useRef(alertComponent);

  const showAlertToast = useCallback(() => {
    const alert = alertRef.current;
    if (alert && alert.__typename === 'Alert') {
      toaster.alertToast({
        title: alert.heading?.locale || '',
        message: alert.body?.locale || '',
        learnMore: alert.learnMoreText?.locale || '',
        learnMoreUrl: alert.learnMoreUrl?.locale || '',
        options: {
          duration: TOAST_DURATION,
        },
      });
    }
  }, []);

  const { addAppStateChangeListeners, removeAppStateChangeListeners } = useAppStateChangeListener();

  // Show toast once alertComponent data is available (covers initial load)
  useEffect(() => {
    showAlertToast();
  }, [alertComponent, showAlertToast]);

  // Show toast every time the user returns to the app from background
  useEffect(() => {
    addAppStateChangeListeners(showAlertToast);
    return () => {
      removeAppStateChangeListeners();
    };
  }, [showAlertToast, addAppStateChangeListeners, removeAppStateChangeListeners]);
};
